/**
 * Helpers for the custom Firebase Auth action handler (/password-reset/confirm).
 *
 * Firebase builds every auth email link against a single project-wide action
 * URL (`callbackUri`) and appends `mode`, `oobCode`, `apiKey`, `continueUrl`
 * and `lang`. That one URL is shared by *every* email Firebase sends and by
 * every client — web, iOS and Android.
 *
 * We only take over `resetPassword`, so users set their new password on
 * hiiker.app instead of the Firebase-hosted page. Every other mode
 * (`signIn` for magic-link login, `verifyEmail`, `recoverEmail`,
 * `revertSecondFactorAddition`, plus anything Firebase adds later) is
 * forwarded verbatim to the original Firebase handler, so those flows behave
 * exactly as they did before this page existed. That "forward what we don't
 * own" rule is the important one: magic-link sign-in depends on the handler
 * bouncing the user to `continueUrl`, and breaking it would lock people out.
 */

export interface ActionCodeParams {
  mode: string | null;
  oobCode: string | null;
  continueUrl: string | null;
  lang: string | null;
}

export type ActionRoute =
  /** We handle it: show the set-a-new-password form. */
  | { kind: 'reset'; oobCode: string; continueUrl: string | null }
  /** Not ours: hand straight back to Firebase's own handler, query intact. */
  | { kind: 'forward'; url: string }
  /** Malformed or code-less link: show our own "request a new link" state. */
  | { kind: 'invalid' };

/** The mode this page implements itself. Everything else gets forwarded. */
export const HANDLED_MODE = 'resetPassword';

export function parseActionCodeParams(search: string): ActionCodeParams {
  const params = new URLSearchParams(search || '');
  return {
    mode: params.get('mode'),
    oobCode: params.get('oobCode'),
    continueUrl: params.get('continueUrl'),
    lang: params.get('lang'),
  };
}

/**
 * Build the Firebase-hosted handler URL for a given auth domain.
 * Derived from `window.firebaseConfigs.authDomain` rather than hardcoded so a
 * project change can't silently strand the forwarding path.
 */
export function firebaseHandlerUrl(authDomain: string): string {
  return `https://${authDomain}/__/auth/action`;
}

/**
 * Decide what the page should do with the link the user arrived on.
 *
 * `search` is passed through untouched when forwarding — Firebase's handler
 * needs the original `apiKey`/`continueUrl`/`lang`, and re-encoding them
 * risks mangling the nested `continueUrl`.
 */
export function resolveActionRoute(
  search: string,
  authDomain: string,
): ActionRoute {
  const { mode, oobCode, continueUrl } = parseActionCodeParams(search);

  if (!mode) return { kind: 'invalid' };

  if (mode === HANDLED_MODE) {
    if (!oobCode) return { kind: 'invalid' };
    return { kind: 'reset', oobCode, continueUrl };
  }

  const query = search && search.startsWith('?') ? search : `?${search || ''}`;
  return { kind: 'forward', url: `${firebaseHandlerUrl(authDomain)}${query}` };
}

/**
 * Only ever bounce to a `continueUrl` on our own origin.
 *
 * The parameter is attacker-controllable in the URL, so honouring it blindly
 * after a successful reset would turn this page into an open redirect. Anything
 * off-origin (or unparseable) falls back to null and the caller sends the user
 * to /login instead.
 */
export function safeContinueUrl(
  continueUrl: string | null,
  currentOrigin: string,
): string | null {
  if (!continueUrl) return null;

  try {
    const parsed = new URL(continueUrl, currentOrigin);
    if (parsed.origin !== currentOrigin) return null;
    // Reject non-http(s) schemes (javascript:, data:) that can survive a
    // same-origin check on some engines when the base is applied.
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return null;
    return parsed.toString();
  } catch {
    return null;
  }
}

export interface PasswordValidation {
  valid: boolean;
  error: string | null;
}

/**
 * Mirrors the server-side rules in UsersController#complete_hiiker_api_account_setup
 * so both password-setting surfaces reject the same things with the same words.
 * Firebase's own minimum is also 6 characters.
 */
export function validateNewPassword(
  password: string,
  confirmation: string,
): PasswordValidation {
  if (!password) {
    return { valid: false, error: 'Please choose a password.' };
  }
  if (password.length < 6) {
    return { valid: false, error: 'Password must be at least 6 characters long.' };
  }
  if (password !== confirmation) {
    return { valid: false, error: 'Password confirmation does not match.' };
  }
  return { valid: true, error: null };
}
